import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { COLORS } from '../../molecules/logs/logsConstants';
import { isPlaceholder } from '../../molecules/logs/relaxedParser';

const AUTO_EXPAND_DEPTH = 1;
const PAGE_SIZE = 40;
const MAX_STRING_PREVIEW = 150;

const useStyles = makeStyles(() => ({
  node: {
    fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
    fontSize: 12.5,
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  toggle: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    userSelect: 'none',
  },
  toggleIcon: {
    fontSize: 16,
    color: COLORS.textMuted,
    flexShrink: 0,
    marginTop: 1,
  },
  toggleSpacer: {
    width: 16,
    flexShrink: 0,
  },
  key: {
    color: '#0F766E', 
    fontWeight: 600,
    whiteSpace: 'nowrap', 
  },
  bracket: {
    color: COLORS.textMuted,
  },
  summary: {
    color: COLORS.textMuted,
    fontStyle: 'italic',
    marginLeft: 4,
  },
  string: { color: '#B91C1C', overflowWrap: 'anywhere' },  // anywhere-wrap handles pathological unbroken-character strings gracefully
  placeholder: {
    color: COLORS.textMuted,
    fontStyle: 'italic',
    backgroundColor: '#F1F5F9',
    borderRadius: 3,
    padding: '0 4px',
  },
  number: { color: '#1D4ED8' },
  boolean: { color: '#7C3AED' },
  null: { color: COLORS.textMuted, fontStyle: 'italic' },
  showMoreBtn: {
    color: COLORS.accent,
    cursor: 'pointer',
    fontSize: 12,
    marginLeft: 16,
    fontWeight: 600,
    background: 'none',
    border: 'none',
    padding: '4px 0',
  },
  stringMoreBtn: {
    color: COLORS.accent,
    cursor: 'pointer',
    fontWeight: 600,
    marginLeft: 4,
  },
}));


function PrimitiveValue({ value, classes }) {
  const [expanded, setExpanded] = useState(false);

  if (value === null) return <span className={classes.null}>null</span>;
  if (isPlaceholder(value)) {
    return <span className={classes.placeholder} title="Original data was already summarized/truncated before this log was written — not recoverable here">{value.label} · truncated</span>;
  }

  if (typeof value === 'string') {
    const isLong = value.length > MAX_STRING_PREVIEW;
    const shown = expanded || !isLong ? value : `${value.slice(0, MAX_STRING_PREVIEW)}`;
    return (
      <span className={classes.string}>
        "{shown}
        {isLong && !expanded && (
          <>
            …"{' '}
            <span className={classes.stringMoreBtn} onClick={(e) => { e.stopPropagation(); setExpanded(true); }}>
              show more
            </span>
          </>
        )}
        {(!isLong || expanded) && '"'}
      </span>
    );
  }

  if (typeof value === 'number') return <span className={classes.number}>{value}</span>;
  if (typeof value === 'boolean') return <span className={classes.boolean}>{String(value)}</span>;
  return <span>{String(value)}</span>;
}


const SIMPLE_MAX_ENTRIES = 6;
function isSimpleLeaf(value) {
  const entries = Array.isArray(value) ? value : Object.values(value);
  if (entries.length > SIMPLE_MAX_ENTRIES) return false;
  return entries.every((v) => v === null || typeof v !== 'object' || isPlaceholder(v));
}

export default function JsonTreeNode({ keyName, value, depth = 0 }) {
  const classes = useStyles();
  const isObject = value !== null && typeof value === 'object' && !isPlaceholder(value);
  const [expanded, setExpanded] = useState(
    depth < AUTO_EXPAND_DEPTH || (isObject && isSimpleLeaf(value))
  );
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  if (!isObject) {
    return (
      <div className={`${classes.node} ${classes.row}`} style={{ paddingLeft: depth * 16 }}>
        <span className={classes.toggleSpacer} />
        {keyName != null && <span className={classes.key}>{keyName}: </span>}
        <PrimitiveValue value={value} classes={classes} />
      </div>
    );
  }

  const isArray = Array.isArray(value);
  const entries = isArray ? value.map((v, i) => [i, v]) : Object.entries(value);
  const [openBracket, closeBracket] = isArray ? ['[', ']'] : ['{', '}'];
  const count = entries.length;
  const visibleEntries = entries.slice(0, visibleCount);
  const hasMore = count > visibleCount;

  return (
    <div className={classes.node} style={{ paddingLeft: depth * 16 }}>
      <div className={classes.row}>
        <span className={classes.toggle} onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}>
          {expanded ? (
            <ExpandMoreIcon className={classes.toggleIcon} />
          ) : (
            <ChevronRightIcon className={classes.toggleIcon} />
          )}
          {keyName != null && <span className={classes.key}>{keyName}: </span>}
          <span className={classes.bracket}>{openBracket}</span>
          {!expanded && (
            <span className={classes.summary}>
              {count} {isArray ? (count === 1 ? 'item' : 'items') : (count === 1 ? 'key' : 'keys')}
            </span>
          )}
          {!expanded && <span className={classes.bracket}>{closeBracket}</span>}
        </span>
      </div>

      {expanded && (
        <>
          {visibleEntries.map(([k, v]) => (
            <JsonTreeNode key={k} keyName={isArray ? null : `"${k}"`} value={v} depth={depth + 1} />
          ))}
          {hasMore && (
            <button
              className={classes.showMoreBtn}
              style={{ marginLeft: (depth + 1) * 16 }}
              onClick={(e) => { e.stopPropagation(); setVisibleCount((c) => c + PAGE_SIZE); }}
            >
              Show {Math.min(PAGE_SIZE, count - visibleCount)} more of {count - visibleCount} remaining…
            </button>
          )}
          <div style={{ paddingLeft: depth * 16 }}>
            <span className={classes.toggleSpacer} style={{ display: 'inline-block' }} />
            <span className={classes.bracket}>{closeBracket}</span>
          </div>
        </>
      )}
    </div>
  );
}